<template>
  <div class="batch-page">
    <div class="container-fluid py-4">
      <!-- Top header + preset input -->
      <header class="bp-header mb-3">
        <h1 class="bp-title mb-1">Workflow Builder · Batch Pipeline Construction</h1>
        <p class="bp-subtitle mb-0">
          Step 1: Configure input types (cannot be modified after locking) → Step 2: Select and configure software → Step 3: Upload files and run the task.
        </p>
        <div class="bp-preset-bar mt-2 d-flex flex-wrap align-items-center gap-2">
          <label class="bp-preset-label mb-0">Preset:</label>
          <input
            v-model="presetKeyword"
            type="text"
            class="form-control form-control-sm bp-preset-input"
            placeholder="Paste a share code (p:...) or enter a preset name/ID"
            @keyup.enter="applyPreset"
          />
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="applyPreset"
            :disabled="!presetKeyword.trim()"
          >
            Apply Preset
          </button>
          <span class="bp-card-caption">Supports: Share Code (p:...), Preset Name, or JSON</span>
        </div>
      </header>

      <!-- Step progress -->
      <div class="bp-stepper mt-2 mb-2">
        <div class="bp-stepper-item is-active">
          <div class="bp-stepper-index">1</div>
          <div class="bp-stepper-text">
            <div class="bp-stepper-title">Configure Inputs</div>
            <div class="bp-stepper-caption">Define the file types and quantities for this task</div>
          </div>
        </div>

        <div class="bp-stepper-divider"></div>

        <div
          class="bp-stepper-item"
          :class="{ 'is-active': inputsLocked, 'is-disabled': !inputsLocked }"
        >
          <div class="bp-stepper-index">2</div>
          <div class="bp-stepper-text">
            <div class="bp-stepper-title">Build Pipeline</div>
            <div class="bp-stepper-caption">Select tools and configure parameters</div>
          </div>
        </div>

        <div class="bp-stepper-divider"></div>

        <div
          class="bp-stepper-item"
          :class="{ 'is-active': pipelineConfirmed, 'is-disabled': !pipelineConfirmed }"
        >
          <div class="bp-stepper-index">3</div>
          <div class="bp-stepper-text">
            <div class="bp-stepper-title">Upload & Run</div>
            <div class="bp-stepper-caption">Upload files or select an example, then run the task</div>
          </div>
        </div>
      </div>

      <!-- Step 1: input configuration -->
      <section class="bp-card mb-4" :class="{ 'bp-card--active': !inputsLocked }">
        <div class="bp-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div class="bp-card-title-sm">
              Step 1 · Input File Configuration
              <span v-if="inputsLocked" class="badge bg-success-subtle text-success-emphasis ms-2">Locked</span>
            </div>

            <div class="bp-card-caption" v-if="!inputsLocked">
              Specify the input file types and quantities for this pipeline. Custom types are supported.
            </div>
            <div class="bp-card-caption" v-else>
              The input file types and quantities for this session are fixed. To modify them, please create a new task.
            </div>
          </div>

          <div class="d-flex flex-column align-items-end gap-1">
            <div class="bp-card-caption">
              Currently configured:
              <span v-if="totalInputCount > 0">{{ inputSummaryText }}</span>
              <span v-else class="text-danger">No input file types have been configured yet.</span>
            </div>

            <button
              v-if="!inputsLocked"
              type="button"
              class="btn btn-sm btn-primary"
              @click="confirmInputs"
              :disabled="totalInputCount === 0"
            >
              Confirm Input Files
            </button>

            <button
              v-else
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="reloadBatchPage"
            >
              Create a New Task
            </button>
          </div>
        </div>

        <!-- Step 1 content -->
        <div v-if="!inputsLocked" class="bp-input-config-body">
          <div class="bp-input-section-title mb-1">File Type List</div>

          <div class="bp-input-list">
            <div
              v-for="item in inputTypes"
              :key="item.id"
              class="bp-input-row"
            >
              <div class="bp-input-label">
                <span class="bp-input-name">{{ item.label }}</span>
                <span class="bp-input-key">{{ item.key }}</span>
              </div>

              <div class="bp-input-controls">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="item.count = Math.max((item.count ?? 0) - 1, 0)"
                  :disabled="item.count === 0"
                >-</button>

                <input
                  type="number"
                  min="0"
                  class="form-control form-control-sm bp-input-number"
                  v-model.number="item.count"
                />

                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="item.count = (item.count ?? 0) + 1"
                >+</button>

                <button
                  v-if="!item.builtin"
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="inputTypes = inputTypes.filter(t => t.id !== item.id)"
                >✕</button>
              </div>
            </div>

            <!-- Custom type -->
            <div class="bp-input-row bp-input-row--new mt-2">
              <div class="bp-input-label">
                <span class="bp-input-name">&gt; Add a Custom Type</span>
                <span class="bp-input-key">For example: bw / txt / json</span>

                <div class="bp-input-new-fields mt-1">
                  <input
                    v-model="newCustomLabel"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="File type (e.g., TXT)"
                  />

                  <input
                    v-model="newCustomKey"
                    type="text"
                    class="form-control form-control-sm me-1"
                    placeholder="File extension (e.g., .txt)"
                  />
                </div>
              </div>

              <div class="bp-input-controls bp-input-controls--new">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  @click="addCustomType"
                  :disabled="!newCustomKey.trim() || !newCustomLabel.trim()"
                >Add</button>
              </div>
            </div>
          </div>

          <div class="bp-card-caption mt-2">Note: Input file configuration will be locked after confirmation.</div>
        </div>
      </section>

      <section v-if="!pipelineConfirmed" class="bp-step2-wrapper position-relative">
        <div
          v-if="!inputsLocked"
          class="bp-step2-overlay d-flex flex-column justify-content-center align-items-center text-center"
        >
          <div class="bp-step2-overlay-box">
            <div class="bp-step2-overlay-title mb-1">Please complete Step 1 first</div>
            <div class="bp-step2-overlay-text mb-2">Please configure and confirm the input file types and quantities above.</div>

            <button type="button" class="btn btn-sm btn-outline-light" @click="scrollToStep1">
              Back to Input Configuration
            </button>
          </div>
        </div>

        <div class="row gx-3 gy-3">
          <!-- Left: categories -->
          <aside class="col-12 col-lg-2">
            <div class="bp-card h-100">
              <div class="bp-card-header">
                <div class="bp-card-title-sm">Step 2 · Tool Categories</div>
                <div class="bp-card-caption">Browse tools by functionality</div>
              </div>

              <ul class="bp-category-list">
                <li
                  class="bp-category-item"
                  :class="{ active: activeCategory === null }"
                  @click="activeCategory = null"
                >
                  <span>All Tools</span>
                  <span class="bp-category-count">{{ toolkitMergeList.length }}</span>
                </li>

                <li
                  v-for="cat in categories"
                  :key="cat.key"
                  class="bp-category-item"
                  :class="{ active: activeCategory === cat.key }"
                  @click="activeCategory = cat.key"
                >
                  <span class="bp-category-name">{{ cat.name }}</span>
                  <span class="bp-category-count">{{ cat.count }}</span>
                </li>
              </ul>
            </div>
          </aside>

          <!-- Middle: pipeline editor -->
          <main class="col-12 col-lg-6">
            <div class="bp-card h-100 d-flex flex-column">
              <div class="bp-card-header d-flex justify-content-between align-items-center">
                <div>
                  <div class="bp-card-title-sm">Current Pipeline</div>
                  <div class="bp-card-caption">You can continue adjusting parameters for the added steps</div>
                </div>
                <span class="badge bg-light text-muted border">Steps: {{ steps.length }}</span>
              </div>

              <div class="bp-cart-body flex-grow-1">
                <div v-if="!steps.length" class="bp-empty">
                  <p class="mb-1">The pipeline is empty</p>
                  <p class="bp-card-caption mb-0">Add steps from the tool store on the right</p>
                </div>

                <ul v-else class="bp-cart-list">
                  <li
                    v-for="(step, index) in steps"
                    :key="step.id || index"
                    class="bp-cart-item"
                  >
                    <div class="bp-cart-index">{{ index + 1 }}</div>

                    <div class="bp-cart-main">
                      <div class="bp-cart-title">
                        {{ step.name || (step.payload && step.payload.software_name) || 'Unnamed Step' }}
                      </div>

                      <div class="bp-cart-meta">
                        <span v-if="step.payload && step.payload.feature" class="bp-tag">{{ step.payload.feature }}</span>
                        <span v-if="step.payload.sequencing_mode" class="bp-tag ms-1">{{ step.payload.sequencing_mode }}</span>
                        <span v-if="step.payload.comment" class="text-muted small ms-1">{{ step.payload.comment }}</span>
                      </div>

                      <!-- Step parameter panel -->
                      <div v-if="step.payload" class="step-config-panel mt-2">

                        <!-- ① Text parameters -->
                        <div
                          v-for="paramName in Object.keys((step.payload.params || {}))"
                          :key="`step-${index}-param-${paramName}`"
                          class="step-field-row d-flex align-items-center"
                        >
                          <label class="form-label form-label-sm step-field-label text-nowrap mb-0 me-2">
                            {{ paramName }}
                          </label>

                          <input
                            v-model="step.payload.params[paramName]"
                            type="text"
                            class="form-control form-control-sm step-field-control flex-grow-1"
                            :placeholder="'Please enter ' + paramName + '  (default: empty)'"
                            :title="paramName"
                            :disabled="pipelineConfirmed"
                          />
                        </div>

                        <!-- ② Select parameters -->
                        <div
                          v-for="(groupMap, optionKey) in (step.payload.selectableOptions || {})"
                          :key="`step-${index}-select-${optionKey}`"
                        >
                          <div
                            v-for="(options, groupKey) in groupMap"
                            :key="`step-${index}-${optionKey}-${groupKey}`"
                            class="step-field-row d-flex align-items-center"
                          >
                            <label class="form-label form-label-sm step-field-label text-nowrap mb-0 me-2">
                              {{ optionKey }} | {{ groupKey }}
                            </label>

                            <select
                              v-model="step.payload.selected[optionKey][groupKey]"
                              class="form-control form-control-sm step-field-control flex-grow-1"
                              :title="`${optionKey}-${groupKey}`"
                              :disabled="pipelineConfirmed"
                              @change="updateSelectOption(step.payload, optionKey, groupKey)"
                            >
                              <option v-for="option in options" :key="option" :value="option">
                                {{ option }}
                              </option>
                            </select>
                          </div>
                        </div>

                        <!-- ③ Index parameters -->
                        <div
                          v-if="step.payload.selectableIndex && Object.keys(step.payload.selectableIndex).length"
                          class="mt-2"
                        >
                          <div class="bp-card-caption mb-1">Index selection</div>

                          <!-- ref -->
                          <template v-if="step.payload.selectableIndex.ref && Object.keys(step.payload.selectableIndex.ref).length">
                            <div class="step-field-row d-flex align-items-center">
                              <label class="form-label form-label-sm step-field-label text-nowrap mb-0 me-2">
                                ref_species
                              </label>

                              <select
                                v-model="step.payload.selected_index.ref_species"
                                class="form-control form-control-sm step-field-control flex-grow-1"
                                :disabled="pipelineConfirmed"
                              >
                                <option
                                  v-for="species in Object.keys(step.payload.selectableIndex.ref)"
                                  :key="species"
                                  :value="species"
                                >
                                  {{ species }}
                                </option>
                              </select>
                            </div>

                            <div class="step-field-row d-flex align-items-center">
                              <label class="form-label form-label-sm step-field-label text-nowrap mb-0 me-2">
                                ref_release
                              </label>

                              <select
                                v-model="step.payload.selected_index.ref_release"
                                class="form-control form-control-sm step-field-control flex-grow-1"
                                :disabled="pipelineConfirmed"
                              >
                                <option
                                  v-for="release in (step.payload.selectableIndex.ref[step.payload.selected_index.ref_species] || [])"
                                  :key="release"
                                  :value="release"
                                >
                                  {{ release }}
                                </option>
                              </select>
                            </div>
                          </template>

                          <!-- other index keys -->
                          <template
                            v-for="(options, idxKey) in step.payload.selectableIndex"
                            :key="`step-${index}-index-${idxKey}`"
                          >
                            <div
                              v-if="idxKey !== 'ref'"
                              class="step-field-row d-flex align-items-center"
                            >
                              <label class="form-label form-label-sm step-field-label text-nowrap mb-0 me-2">
                                {{ idxKey }}
                              </label>

                              <select
                                v-model="step.payload.selected_index[idxKey]"
                                class="form-control form-control-sm step-field-control flex-grow-1"
                                :disabled="pipelineConfirmed"
                              >
                                <option v-for="opt in options" :key="opt" :value="opt">
                                  {{ opt }}
                                </option>
                              </select>
                            </div>
                          </template>
                        </div>
                      </div>

                    </div>
                  </li>
                </ul>

              </div>
            </div>
          </main>

          <!-- Right: tool store -->
          <section class="col-12 col-lg-4">
            <WorkflowComponent
              v-model:stepIndex="stepIndex"
              :toolkitMergeList="toolkitMergeList"
              :active-category="activeCategory"
              :initial-pool="initialPool"
              :steps="steps"
              @update:steps="steps = $event"
            />
          </section>
        </div>
      </section>

      <!-- Step 2 collapsed view -->
      <section
        v-else
        class="bp-card mb-3"
      >
        <div class="bp-card-header d-flex justify-content-between align-items-center">
          <div>
            <div class="bp-card-title-sm">
              Step 2 · Build Pipeline (Completed)
            </div>
            <div class="bp-card-caption">
              The software and parameters have been confirmed. To make changes, you can re-edit the pipeline.
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="pipelineConfirmed = false"
          >
            Re-edit Pipeline
          </button>
        </div>
        <div class="bp-review-body bp-review-body--steps">
          <div class="bp-review-block">
            <div class="bp-review-title">Current Step Summary</div>
            <ul class="bp-review-steps">
              <li
                v-for="(step, index) in steps"
                :key="step.id || index"
                class="bp-review-step-item"
              >
                <div class="bp-review-step-index">{{ index + 1 }}</div>
                <div class="bp-review-step-main">
                  <div class="bp-review-step-title">
                    {{ step.payload.software_name }}
                    <span class="bp-tag ms-1">{{ step.payload.feature }}</span>
                  </div>
                  <div
                    v-if="getStepValueList(step).length"
                    class="bp-review-step-meta"
                  >
                    {{ getStepValueList(step).join(', ') }}
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- Step 2 action bar -->
      <section
        class="bp-step2-actions mt-3 mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2"
      >
        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-outline-primary btn-sm"
            @click="confirmPipeline"
            :disabled="!inputsLocked || steps.length === 0 "
          >
            Confirm Pipeline
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            @click="previewSteps"
            :disabled="steps.length === 0 || !inputsLocked"
          >
            Preview Pipeline Code
          </button>
        </div>
        <div class="bp-card-caption">
          After confirming the parameters, Step 3 (Upload & Run) will be unlocked.
          The JSON preview is printed to the console only.
        </div>
      </section>

      <div
        v-if="showPreviewModal"
        class="bp-modal-backdrop"
        @click.self="closePreviewModal"
      >
        <div class="bp-modal bp-modal-lg">
          <div class="bp-modal-header d-flex justify-content-between align-items-center">
            <div>
              <div class="bp-modal-title">Pipeline Code Preview</div>
              <div class="bp-modal-subtitle">Read-only Snakemake workflow generated from the current configuration</div>
            </div>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="closePreviewModal">
              ✕
            </button>
          </div>

          <div class="bp-modal-body">
            <div v-if="previewLoading" class="text-center text-muted py-4">
              Generating pipeline code, please wait…
            </div>

            <div v-else>
              <div
                v-if="previewError"
                class="alert alert-danger py-2 px-3 mb-2"
              >
                {{ previewError }}
              </div>

              <div class="bp-code-wrapper">
                <pre class="bp-code-block"><code>{{ previewCode }}</code></pre>
              </div>

            </div>
          </div>

          <div class="bp-modal-footer d-flex justify-content-between align-items-center">
            <span class="bp-card-caption mb-0">
              Note: This preview only shows the pipeline code for the current session and does not submit the task.
            </span>
            <div class="d-flex gap-2">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="copyPreviewCode"
                :disabled="!previewCode"
              >
                Copy Code
              </button>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="closePreviewModal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: upload files & run task -->
      <section
        class="bp-card"
        :class="{ 'bp-card--disabled': !pipelineConfirmed }"
      >
        <div class="bp-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div class="bp-card-title-sm">Step 3 · Upload Files & Run Task</div>
            <div class="bp-card-caption">
              A default sample group is included. Clicking “Add Sample Group” will add one slot for each variable under all input file types.
              Each sample group can be renamed. All sample names must be non-empty and unique before submission.
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="addSampleGroup"
              :disabled="!pipelineConfirmed || !initialPool"
            >
              Add Sample Group
            </button>
            <span class="badge bg-light text-muted border">
              {{ pipelineConfirmed ? 'Step 2 Completed' : 'Waiting for Step 2 completion' }}
            </span>
          </div>
        </div>

        <!-- Step 2 completed: show per-sample binding UI -->
        <div
          v-if="pipelineConfirmed"
          class="bp-review-body"
        >
          <div
            v-if="!initialPool || Object.keys(initialPool).length === 0 || sampleSlotList.length === 0"
            class="bp-empty text-center"
          >
            <p class="mb-1">No input file configuration found from Step 1</p>
            <p class="bp-card-caption mb-0">
              Please go back to Step 1 to configure and confirm the input file types.
            </p>
          </div>

          <div v-else>
            <!-- Render by sample group -->
            <div
              v-for="(slotName, idx) in sampleSlotList"
              :key="slotName"
              class="bp-sample-block mb-3"
            >
              <!-- Sample group header -->
              <div class="d-flex flex-wrap align-items-center mb-2">
                <div class="bp-review-title mb-0 me-3">
                  Sample Group {{ idx + 1 }}
                </div>
                <label class="bp-card-caption me-1 mb-0">
                  Sample Name:
                </label>
                <input
                  v-model="sampleNameBySlot[slotName]"
                  type="text"
                  class="form-control form-control-sm"
                  style="max-width: 200px;"
                  :placeholder="idx === 0 ? 'sample' : ('sample' + (idx + 1))"
                />
                <span class="bp-card-caption ms-2 text-muted">
                  This sample name is shared across all file types (editable).
                </span>

                <!-- Remove sample group (only if > 1 group) -->
                <button
                  v-if="sampleSlotList.length > 1"
                  type="button"
                  class="btn btn-sm btn-outline-danger ms-auto"
                  @click="removeSampleGroup(slotName)"
                >
                  Remove Group
                </button>
              </div>

              <div class="bp-card-caption mb-1">
                Bind each variable under every file type for this sample group:
              </div>

              <!-- Each type + variable name -->
              <div
                v-for="(varList, typeKey) in initialPool"
                :key="typeKey"
                class="mb-2"
              >
                <div class="bp-slot-type-label mb-1">
                  Type: <strong>{{ typeKey }}</strong>
                </div>

                <div
                  v-for="varName in varList"
                  :key="typeKey + '::' + varName + '::' + slotName"
                  class="bp-slot-row d-flex flex-wrap align-items-center mb-2"
                >
                  <div class="bp-slot-label me-2">
                    Variable Name: <strong>{{ varName }}</strong>
                  </div>

                  <!-- Mode: upload / server example -->
                  <select
                    v-model="fileBindings[typeKey][varName][slotName].mode"
                    class="form-select form-select-sm me-2"
                    style="max-width: 140px;"
                  >
                    <option :value="null">Select Source</option>
                    <option value="server">Example File</option>
                    <option value="upload">Upload File</option>
                  </select>

                  <!-- Mode: upload -->
                  <template v-if="fileBindings[typeKey][varName][slotName].mode === 'upload'">
                    <input
                      type="file"
                      class="form-control form-control-sm me-2"
                      style="max-width: 260px;"
                      @change="onSlotFileChange($event, typeKey, varName, slotName)"
                    />
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary me-2"
                      @click="uploadSlotFile(typeKey, varName, slotName)"
                      :disabled="!fileBindings[typeKey][varName][slotName].uploadFile || !sessionId"
                    >
                      Upload to Server
                    </button>
                    <span class="bp-card-caption">
                      {{ getSlotUploadStatusText(typeKey, varName, slotName) }}
                    </span>
                  </template>

                  <!-- Mode: server example -->
                  <template v-else-if="fileBindings[typeKey][varName][slotName].mode === 'server'">
                    <select
                      v-model="fileBindings[typeKey][varName][slotName].serverSampleId"
                      class="form-select form-select-sm me-2"
                      style="max-width: 260px;"
                      @focus="loadServerSamples(typeKey)"
                    >
                      <option :value="null">Select an example file</option>
                      <option
                        v-for="sample in serverSamples[typeKey] || []"
                        :key="sample.id"
                        :value="sample.id"
                      >
                        {{ sample.name }}
                      </option>
                    </select>
                    <span class="bp-card-caption">
                      Run the task using an existing example file on the server.
                    </span>
                  </template>

                  <!-- Mode not selected -->
                  <template v-else>
                    <span class="bp-card-caption text-muted">
                      Please select either “Upload File” or “Example File”.
                    </span>
                  </template>
                </div>
              </div>
            </div>

            <!-- Bottom: run button -->
            <div class="d-flex justify-content-between align-items-center mt-3">
              <div class="bp-card-caption">
                All sample names must be non-empty and unique. In addition, all variables under all file types must be bound for each sample group before running the task.
              </div>
              <button
                type="button"
                class="btn btn-sm btn-success"
                @click="runTask"
                :disabled="!canRunTask"
              >
                Run Task
              </button>
            </div>
          </div>
        </div>
        <!-- Step 2 not completed -->
        <div
          v-else
          class="bp-empty text-center"
        >
          <p class="mb-1">Please complete Step 2 · Build Pipeline first</p>
          <p class="bp-card-caption mb-0">
            The Upload & Run section will be unlocked automatically after the software and parameters are confirmed.
          </p>
        </div>
      </section>

      <div
        v-if="showRunModal"
        class="bp-modal-backdrop"
        @click.self="closeRunModal"
      >
        <div class="bp-modal bp-modal-xl">
          <div class="bp-modal-header d-flex justify-content-between align-items-center">
            <div>
              <div class="bp-modal-title">Confirm Before Running</div>
              <div class="bp-modal-subtitle">
                Below is the complete pipeline code generated for this task (including sample bindings).
                Please review it carefully before execution.
              </div>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="closeRunModal"
              :disabled="runSubmitting"
            >
              ✕
            </button>
          </div>

          <div class="bp-modal-body">
            <div v-if="runPreviewLoading" class="text-center text-muted py-4">
              Generating run preview code, please wait…
            </div>

            <div v-else>
              <div
                v-if="runPreviewError"
                class="alert alert-danger py-2 px-3 mb-2"
              >
                {{ runPreviewError }}
              </div>

              <div class="bp-code-wrapper">
                <pre class="bp-code-block"><code>{{ runPreviewCode }}</code></pre>
              </div>

            </div>
          </div>

          <div class="bp-modal-footer d-flex justify-content-between align-items-center">
            <span class="bp-card-caption mb-0">
              Note: Clicking “Run Task” will submit the job to the backend for execution and may redirect you to the results or job status page.
            </span>
            <div class="d-flex gap-2">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="copyRunCode"
                :disabled="!runPreviewCode"
              >
                Copy Code
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="closeRunModal"
                :disabled="runSubmitting"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-sm btn-success"
                @click="confirmRunTask"
                :disabled="runSubmitting || !runPreviewCode || !canRunTask"
              >
                {{ runSubmitting ? 'Running…' : 'Run Task' }}
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import WorkflowComponent from './WorkflowComponent.vue';
import API_BASE_URL from '@/api';
import axios from 'axios';

let nextId = 1;
const genId = () => nextId++;

export default {
  name: 'BatchPage',
  components: {
    WorkflowComponent,
  },
  props: ['taskId'],
  mounted() {
    this.consumePresetFromRoute();
    this.fetchToolkitMergeList();
  },
  data() {
    return {
      stepIndex: 0,
      presetKeyword: '',
      inputsLocked: false,
      inputTypes: [
        { id: genId(), key: '.fq.gz', label: 'FASTQ (single-end)', type: 'fq', count: 0, builtin: true },
        { id: genId(), key: '_1.fq.gz', label: 'FASTQ (pair-end·R1)', type: 'fq1', count: 0, builtin: true },
        { id: genId(), key: '_2.fq.gz', label: 'FASTQ (pair-end·R2)', type: 'fq2', count: 0, builtin: true },
        { id: genId(), key: '.bam', label: 'BAM', type: 'bam', count: 0, builtin: true },
        { id: genId(), key: '.rds', label: 'RDS', type: 'rds', count: 0, builtin: true },
        { id: genId(), key: '.bed', label: 'BED', type: 'bed', count: 0, builtin: true },
        { id: genId(), key: '.vcf', label: 'VCF', type: 'vcf', count: 0, builtin: true },
      ],
      newCustomKey: '',
      newCustomLabel: '',

      pipelineConfirmed: false,
      categories: [],
      activeCategory: null,
      toolkitMergeList: [],
      steps: [],
      initialPool: null,

      sessionId: this.taskId || null,
      sampleSlotList: [],         // slot list for sample groups, e.g. ['slot1', 'slot2', ...]
      sampleNameBySlot: {},       // { [slotName]: 'sample' | 'sample2' | ... }
      fileBindings: {},
      serverSamples: {},          // { [typeKey]: [ { id, name, ... } ] }

      showPreviewModal: false,
      previewLoading: false,
      previewCode: '',
      previewError: '',

      showRunModal: false,
      runPreviewLoading: false,
      runPreviewCode: '',
      runPreviewError: '',
      runSubmitting: false,
    };
  },
  watch: {
    "$route.query.preset"() {
      this.consumePresetFromRoute();
    },
    // Whenever steps updates, ensure each step has default selected_index values
    steps: {
      deep: true,
      handler(newSteps) {
        (newSteps || []).forEach(step => {
          this.normalizeStepIndex(step);
        });
      },
    },
  },
  computed: {
    totalInputCount() {
      return this.inputTypes.reduce((sum, t) => sum + (t.count || 0), 0);
    },
    inputSummaryText() {
      return this.inputTypes
        .filter((t) => t.count > 0)
        .map((t) => `${t.label} × ${t.count}`)
        .join(', ');
    },
    // Can run: all sample names valid & all type/var/slot bindings complete
    canRunTask() {
      if (!this.pipelineConfirmed || !this.initialPool || !this.sessionId) return false;
      // 1) sample names: non-empty + unique
      const names = [];
      for (const slotName of this.sampleSlotList) {
        const raw = (this.sampleNameBySlot[slotName] || '').trim();
        if (!raw) return false;
        names.push(raw);
      }
      const nameSet = new Set(names);
      if (nameSet.size !== names.length) return false;
      // 2) all bindings: type / varName / slot
      const typeKeys = Object.keys(this.initialPool);
      for (const typeKey of typeKeys) {
        const varList = this.initialPool[typeKey] || [];
        for (const varName of varList) {
          const typeBindings = this.fileBindings[typeKey];
          if (!typeBindings) return false;
          const varBindings = typeBindings[varName];
          if (!varBindings) return false;
          for (const slotName of this.sampleSlotList) {
            const bind = varBindings[slotName];
            if (!bind) return false;
            if (!bind.mode) return false;
            if (bind.mode === 'upload') {
              if (!bind.fileId) return false;
            } else if (bind.mode === 'server') {
              if (!bind.serverSampleId) return false;
            } else {
              return false;
            }
          }
        }
      }
      return true;
    },
  },
  methods: {

    async consumePresetFromRoute() {
      const p = this.$route.query.preset;
      if (!p) return;
      // 1) same as v-model input
      this.presetKeyword = p;
      // 2) wait for view update, then apply (same as clicking "Apply Preset")
      await this.$nextTick();
      this.applyPreset();
    },

    async previewSteps() {
      if (!this.inputsLocked) {
        alert('Please complete Step 1 · Input File Configuration first.');
        return;
      }
      if (!this.steps.length) {
        alert('Please add at least one step in Step 2 first.');
        return;
      }
      this.showPreviewModal = true;
      this.previewLoading = true;
      this.previewError = '';
      this.previewCode = '';

      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/batch_code/preview_pipeline/`,
          { steps: this.steps, session_id: this.sessionId, initial_pool: this.initialPool }
        );
        this.previewCode =
          res.data.snakemake_code ||
          res.data.code ||
          '# No pipeline code returned. Please check the API response.';
      } catch (err) {
        console.error('previewSteps failed:', err);
        this.previewError =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to fetch pipeline preview.';
        this.previewCode =
          '# Failed to fetch pipeline preview\n# ' + this.previewError;
      } finally {
        this.previewLoading = false;
      }
    },

    closePreviewModal() {
      this.showPreviewModal = false;
    },

    copyPreviewCode() {
      if (!this.previewCode) return;
      const text = this.previewCode;
      // Prefer Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => {
            alert('Code copied to clipboard.');
          })
          .catch(() => {
            alert('Copy failed. Please select the code manually and copy it.');
          });
        return;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        alert('Code copied to clipboard.');
      } catch (e) {
        alert('Copy failed. Please select the code manually and copy it.');
      } finally {
        document.body.removeChild(textarea);
      }
    },

    updateSelectOption(step, optionKey, groupKey) {
      const selectedValue = step.selected[optionKey][groupKey];
      if (optionKey === "fq1") {
        const matchingValue = selectedValue.replace(/_1\.fq\.gz$/, '_2.fq.gz');
        const options = step.selectableOptions.fq2[groupKey] || [];
        if (options.includes(matchingValue)) {
          step.selected.fq2[groupKey] = matchingValue;
        }
      } else if (optionKey === "fq2") {
        const matchingValue = selectedValue.replace(/_2\.fq\.gz$/, '_1.fq.gz');
        const options = step.selectableOptions.fq1[groupKey] || [];
        if (options.includes(matchingValue)) {
          step.selected.fq1[groupKey] = matchingValue;
        }
      }
    },

    // Fill default selected_index values for each step (ref + other index keys)
    normalizeStepIndex(step) {
      if (!step || !step.payload || !step.payload.selectableIndex) return;
      const payload = step.payload;
      const selectableIndex = payload.selectableIndex || {};

      if (!payload.selected_index || typeof payload.selected_index !== 'object') {
        if (this.$set) {
          this.$set(payload, 'selected_index', {});
        } else {
          payload.selected_index = {};
        }
      }
      const selectedIndex = payload.selected_index;

      // 1) ref: species + release
      const refIndex = selectableIndex.ref;
      if (refIndex && typeof refIndex === 'object') {
        const speciesKeys = Object.keys(refIndex);
        if (speciesKeys.length) {
          if (!selectedIndex.ref_species || !refIndex[selectedIndex.ref_species]) {
            selectedIndex.ref_species = speciesKeys[0];
          }
          const releases = refIndex[selectedIndex.ref_species] || [];
          if (!selectedIndex.ref_release || !releases.includes(selectedIndex.ref_release)) {
            selectedIndex.ref_release = releases[0] || '';
          }
        }
      }

      // 2) other index keys: key → value
      Object.entries(selectableIndex).forEach(([idxKey, options]) => {
        if (idxKey === 'ref') return;
        if (!Array.isArray(options) || !options.length) return;
        if (!selectedIndex[idxKey] || !options.includes(selectedIndex[idxKey])) {
          selectedIndex[idxKey] = options[0];
        }
      });
    },

    scrollToStep1() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    updateInputTypesFromBackend(fileDict) {
      if (!fileDict || typeof fileDict !== 'object') return;
      const typeMap = new Map();
      this.inputTypes.forEach(item => {
        typeMap.set(item.type, item);
        item.count = 0;
      });
      Object.entries(fileDict).forEach(([type, files]) => {
        const count = Array.isArray(files) ? files.length : 0;
        const existed = typeMap.get(type);
        if (existed) {
          existed.count = count;
        } else {
          this.inputTypes.push({
            id: genId(),
            key: '.' + type,
            label: type.toUpperCase(),
            type,
            count,
            builtin: false,
          });
        }
      });
    },

    async applyPreset() {
      const raw = (this.presetKeyword || '').trim();
      if (!raw) return;
      try {
        // Backend recognizes raw as share code / JSON / name/ID and returns page-ready data
        const res = await axios.post(`${API_BASE_URL}/api/batch_code/get_preset/`, {
          keyword: raw,
        });
        const data = res.data || {};
        this.initialPool = data.initial_pool || {};
        this.steps = data.steps || [];
        this.updateInputTypesFromBackend(this.initialPool);
        this.initFileBindings();

        this.inputsLocked = true;
        this.pipelineConfirmed = false;
        this.stepIndex = this.steps.length;
        alert(`Preset loaded: ${data.preset_name || 'OK'}`);
      } catch (err) {
        console.error('applyPreset failed:', err);
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to parse preset.';
        alert(msg);
      }
    },

    reloadBatchPage() {
      window.location.reload();
    },

    async confirmInputs() {
      if (this.totalInputCount < 1) {
        alert('Please configure at least one input file type and its quantity before confirming.');
        return;
      }
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/common/get_initial_pool/`,
          {
            inputTypes: this.inputTypes.map(t => ({
              key: t.key,          // e.g. ".bam" / ".fq.gz"
              type: t.type,        // e.g. "bam" / "fq"
              count: t.count || 0,
              builtin: t.builtin,
            })),
          }
        );
        this.initialPool = response.data.initial_pool || {};
        this.initFileBindings();
        this.inputsLocked = true;
      } catch (err) {
        console.error('Failed to fetch initial_pool:', err);
        alert('Failed to fetch the initial file pool (initial_pool). Please try again later.');
      }
    },

    addCustomType() {
      const key = this.newCustomKey.trim();
      const label = this.newCustomLabel.trim();
      if (!key || !label) return;
      if (this.inputTypes.some(t =>
        t.key.toLowerCase() === key.toLowerCase() ||
        t.label.toLowerCase() === label.toLowerCase()
      )) {
        alert(`The type label "${label}" or suffix "${key}" already exists. Please use a different one.`);
        return;
      }
      this.inputTypes.push({
        id: genId(),
        key,
        label,
        type: label.toLowerCase(),
        count: 0,
        builtin: false,
      });
      this.newCustomKey = '';
      this.newCustomLabel = '';
    },

    async fetchToolkitMergeList() {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/common/get_toolkitMergeList/`, { keyword: 'all' });
        this.toolkitMergeList = response.data.toolkitMergeList || [];
        this.categories = response.data.categories || [];
        this.activeCategory = null;
      } catch (error) {
        console.error('Failed to fetch toolkit list:', error);
      }
    },

    getStepValueList(step) {
      if (!step || !step.payload) return [];
      const payload = step.payload;
      const values = [];
      const params = payload.params || {};
      Object.keys(params).forEach(key => {
        const v = params[key];
        if (v !== undefined && v !== null && v !== '') {
          values.push(v);
        }
      });
      const selected = payload.selected || {};
      Object.keys(selected).forEach(optionKey => {
        const groupMap = selected[optionKey] || {};
        Object.keys(groupMap).forEach(groupKey => {
          const v = groupMap[groupKey];
          if (v !== undefined && v !== null && v !== '') {
            values.push(v);
          }
        });
      });
      return values;
    },

    confirmPipeline() {
      if (!this.inputsLocked) {
        alert('Please complete Step 1 first.');
        return;
      }
      if (this.steps.length === 0) {
        alert('Please add at least one step in Step 2 first.');
        return;
      }
      this.pipelineConfirmed = true;
      alert('The pipeline has been confirmed. To make changes later, click “Edit Pipeline”.');
    },

    // ---------------------------- File upload section --------------------------------------------
    initFileBindings() {
      const bindings = {};
      const nameMap = {};
      const sampleSlots = [];
      if (!this.initialPool) {
        this.fileBindings = {};
        this.sampleNameBySlot = {};
        this.sampleSlotList = [];
        return;
      }
      const typeKeys = Object.keys(this.initialPool);
      if (!typeKeys.length) {
        this.fileBindings = {};
        this.sampleNameBySlot = {};
        this.sampleSlotList = [];
        return;
      }
      const firstSlotName = 'slot1';
      sampleSlots.push(firstSlotName);
      nameMap[firstSlotName] = 'sample'; // default name for first group
      // For every type and variable, create a [slot1] binding structure
      typeKeys.forEach((typeKey) => {
        const varList = this.initialPool[typeKey] || [];
        bindings[typeKey] = {};
        varList.forEach((varName) => {
          bindings[typeKey][varName] = {
            [firstSlotName]: {
              mode: null,           // 'upload' | 'server'
              uploadFile: null,     // File object
              fileId: null,         // backend file_id
              serverSampleId: null, // server example file id
            },
          };
        });
      });
      this.fileBindings = bindings;
      this.sampleNameBySlot = nameMap;
      this.sampleSlotList = sampleSlots;
    },

    addSampleGroup() {
      if (!this.initialPool) return;
      const typeKeys = Object.keys(this.initialPool);
      if (!typeKeys.length) return;
      const newIndex = this.sampleSlotList.length; // 0-based
      const newSlotName = `slot${newIndex + 1}`;
      // 1) update slot list
      this.sampleSlotList = [...this.sampleSlotList, newSlotName];
      // 2) generate a default sample name that does not conflict
      const existingNames = new Set(
        Object.values(this.sampleNameBySlot)
          .map((n) => (n || '').trim())
          .filter(Boolean)
      );
      let num = newIndex + 1;
      let candidate = num === 1 ? 'sample' : `sample${num}`;
      while (existingNames.has(candidate)) {
        num += 1;
        candidate = `sample${num}`;
      }
      this.sampleNameBySlot = {
        ...this.sampleNameBySlot,
        [newSlotName]: candidate,
      };
      // 3) add this slot under every type/var
      const newFileBindings = { ...this.fileBindings };
      typeKeys.forEach((typeKey) => {
        const varList = this.initialPool[typeKey] || [];
        if (!newFileBindings[typeKey]) {
          newFileBindings[typeKey] = {};
        }
        varList.forEach((varName) => {
          const oldVarBindings = newFileBindings[typeKey][varName] || {};
          newFileBindings[typeKey][varName] = {
            ...oldVarBindings,
            [newSlotName]: {
              mode: null,
              uploadFile: null,
              fileId: null,
              serverSampleId: null,
            },
          };
        });
      });
      this.fileBindings = newFileBindings;
    },

    removeSampleGroup(slotName) {
      if (!this.initialPool || !this.sampleSlotList.includes(slotName)) return;
      // do not allow removing down to 0 groups
      if (this.sampleSlotList.length <= 1) {
        return;
      }
      // confirm
      const ok = window.confirm(
        `Are you sure you want to remove “Sample Group ${this.sampleSlotList.indexOf(slotName) + 1}”? Its file bindings will also be removed.`
      );
      if (!ok) return;
      // 1) update slot list
      this.sampleSlotList = this.sampleSlotList.filter(s => s !== slotName);
      // 2) update name map
      const nameMap = { ...this.sampleNameBySlot };
      delete nameMap[slotName];
      this.sampleNameBySlot = nameMap;
      // 3) remove slot from fileBindings
      const newFileBindings = {};
      Object.keys(this.fileBindings || {}).forEach((typeKey) => {
        const varMap = this.fileBindings[typeKey] || {};
        newFileBindings[typeKey] = {};
        Object.keys(varMap).forEach((varName) => {
          const slotMap = varMap[varName] || {};
          const slotCopy = { ...slotMap };
          delete slotCopy[slotName];
          newFileBindings[typeKey][varName] = slotCopy;
        });
      });
      this.fileBindings = newFileBindings;
    },

    onSlotFileChange(event, typeKey, varName, slotName) {
      const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
      const bind = this.fileBindings?.[typeKey]?.[varName]?.[slotName];
      if (!bind) return;
      bind.uploadFile = file;
      bind.fileId = null; // re-upload required after reselect
      console.log('file:', this.fileBindings);
    },

    async uploadSlotFile(typeKey, varName, slotName) {
      const bind = this.fileBindings?.[typeKey]?.[varName]?.[slotName];
      if (!bind) return;
      if (!bind.uploadFile) {
        alert('Please select a file to upload first.');
        return;
      }
      if (!this.sessionId) {
        alert('Missing sessionId. Please confirm the input configuration again in Step 1.');
        return;
      }
      const formData = new FormData();
      formData.append('file', bind.uploadFile);
      formData.append('type', typeKey);
      formData.append('var_name', varName);
      formData.append('slot', slotName);
      formData.append('session_id', this.sessionId);
      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/files/upload/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        bind.fileId = res.data.file_id;
        alert(`Slot ${typeKey}:${varName}:${slotName} uploaded successfully`);
      } catch (err) {
        console.error('Upload failed:', err);
        alert('Upload failed. Please try again later.');
      }
    },

    getSlotUploadStatusText(typeKey, varName, slotName) {
      const bind = this.fileBindings?.[typeKey]?.[varName]?.[slotName];
      if (!bind) return '';
      if (!bind.uploadFile) return 'No file selected';
      if (!bind.fileId) return `Selected: ${bind.uploadFile.name} (not uploaded yet)`;
      return `Bound server file: ${bind.uploadFile.name} (ID: ${bind.fileId})`;
    },

    async loadServerSamples(typeKey) {
      if (this.serverSamples[typeKey]) {
        return;
      }
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/files/server_samples/`,
          {
            params: { type: typeKey },
          }
        );
        const samples = res.data.samples || [];
        this.serverSamples = {
          ...this.serverSamples,
          [typeKey]: samples,
        };
      } catch (err) {
        console.error('Failed to fetch server example files:', err);
        alert(`Failed to fetch example files for ${typeKey}. Please try again later.`);
      }
    },

    normalizeSampleName(name) {
      return (name || '')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[\\/?%*:|"<>]/g, '_');
    },

    buildSubmitBindingsBySampleName() {
      const out = {};
      const fb = this.fileBindings || {};
      const nameMap = this.sampleNameBySlot || {};
      Object.keys(fb).forEach(typeKey => {
        out[typeKey] = {};
        Object.keys(fb[typeKey] || {}).forEach(varName => {
          out[typeKey][varName] = {};
          Object.keys(fb[typeKey][varName] || {}).forEach(slotName => {
            const b = fb[typeKey][varName][slotName] || {};
            const rawName = nameMap[slotName];
            const sampleName = this.normalizeSampleName(rawName);
            out[typeKey][varName][sampleName] = {
              mode: b.mode,
              file_id: b.fileId || null,
              server_sample_id: b.serverSampleId || null,
            };
          });
        });
      });
      return out;
    },

    getInputsObject() {
      const obj = {};
      this.inputTypes.forEach((t) => {
        obj[t.key] = t.count || 0;
      });
      return obj;
    },

    async runTask() {
      if (!this.canRunTask) {
        alert('Please fill in all sample names and bind files for every type/variable in every sample group before running.');
        return;
      }
      if (!this.sessionId) {
        alert('Missing sessionId. Please confirm the input configuration again in Step 1.');
        return;
      }
      this.showRunModal = true;
      this.runPreviewLoading = true;
      this.runPreviewError = '';
      this.runPreviewCode = '';
      const payload = {
        session_id: this.sessionId,
        steps: this.steps,
        bindings: this.buildSubmitBindingsBySampleName(),
        initial_pool: this.initialPool,
      };
      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/batch_code/run_batch/`,
          payload
        );
        this.runPreviewCode =
          res.data.snakemake_code ||
          res.data.code ||
          '# No run preview code returned. Please check the API response.';
      } catch (err) {
        console.error('preview_run failed:', err);
        this.runPreviewError =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to generate run preview.';
        this.runPreviewCode =
          '# Failed to generate run preview\n# ' + this.runPreviewError;
      } finally {
        this.runPreviewLoading = false;
      }
    },

    async confirmRunTask() {
      if (!this.canRunTask) {
        alert('The current configuration does not meet the requirements to run. Please check sample names and file bindings.');
        return;
      }
      if (!this.sessionId) {
        alert('Missing sessionId. Please confirm the input configuration again in Step 1.');
        return;
      }
      const payload = {
        session_id: this.sessionId,
        steps: this.steps,
        bindings: this.buildSubmitBindingsBySampleName(),
        initial_pool: this.initialPool,
      };
      this.runSubmitting = true;
      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/batch_code/run_task/`,
          payload
        );
        if (res.status === 200 && (res.data?.ok === true || res.data?.success === true)) {
          const taskId =
            res.data.task_id ||
            this.taskId ||
            this.sessionId;
          if (!taskId) {
            console.warn('run_batch succeeded but task_id is missing:', res.data);
            alert('The task has been submitted, but no task ID was returned. Please contact the administrator or check the logs.');
            this.showRunModal = false;
            return;
          }
          alert('The task has been submitted and is now running. You will be redirected to the task query page.');
          this.showRunModal = false;
          this.$router.push({
            name: 'QueryTask',
            params: { taskId },
          });
        } else {
          console.warn('run_batch returned an unexpected response:', res.data);
          alert('Task submission failed. Please check the console logs.');
        }
      } catch (err) {
        console.error('Task run failed:', err);
        alert('Run failed. Please try again later.');
      } finally {
        this.runSubmitting = false;
      }
    },

    closeRunModal() {
      if (this.runSubmitting) return;
      this.showRunModal = false;
    },

    copyRunCode() {
      if (!this.runPreviewCode) return;
      const text = this.runPreviewCode;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => {
            alert('Code copied to clipboard.');
          })
          .catch(() => {
            alert('Copy failed. Please select the code manually and copy it.');
          });
        return;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        alert('Code copied to clipboard.');
      } catch (e) {
        alert('Copy failed. Please select the code manually and copy it.');
      } finally {
        document.body.removeChild(textarea);
      }
    },

  },
};
</script>

<style scoped src="./BatchPage.css"></style>

